/**
 * @fileoverview Helper Content Renderer Component
 * @module HelperContent
 * 
 * Business Context:
 * - Renders the educational content within the helper blade
 * - Displays conditional sections based on available content
 * - Provides visual examples, instructions, and documentation tips
 * 
 * Technical Context:
 * - Flexible rendering based on content availability
 * - Styled sections with category-appropriate colors
 * - Image gallery support for visual examples
 */

import React, { useState } from 'react';
import { 
  BookOpen, 
  MapPin, 
  ListChecks, 
  Eye, 
  Camera, 
  FileText, 
  AlertTriangle,
  Lightbulb,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import type { HelperContentData } from './helper-content';
import type { MaintenanceCategory } from './HelperBlade';

interface HelperContentProps {
  content: HelperContentData;
  category: MaintenanceCategory;
}

// Section wrapper component for consistent styling
const Section: React.FC<{
  title: string;
  icon: React.ElementType;
  children: React.ReactNode;
  defaultExpanded?: boolean;
}> = ({ title, icon: Icon, children, defaultExpanded = true }) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  
  return (
    <div className="border-b border-slate-100 last:border-0">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Icon className="w-4 h-4 text-slate-500" />
          <span className="font-semibold text-slate-800">{title}</span>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4 text-slate-400" />
        ) : (
          <ChevronDown className="w-4 h-4 text-slate-400" />
        )}
      </button>
      {isExpanded && (
        <div className="px-5 pb-4">
          {children}
        </div>
      )}
    </div>
  );
};

// Single image component with fallback state
const HelperImage: React.FC<{ 
  image: { src: string; alt: string; caption: string }; 
}> = ({ image }) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  
  return (
    <div className="rounded-lg overflow-hidden border border-slate-200">
      <div className="aspect-video bg-slate-100 relative flex items-center justify-center">
        {!imageError ? (
          <>
            {!imageLoaded && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center p-4">
                  <Camera className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                  <p className="text-sm text-slate-400">Loading...</p>
                </div>
              </div>
            )}
            <img 
              src={image.src} 
              alt={image.alt}
              className={`w-full h-full object-cover transition-opacity ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
              onLoad={() => setImageLoaded(true)}
              onError={() => setImageError(true)}
            />
          </>
        ) : (
          <div className="text-center p-4">
            <Camera className="w-10 h-10 mx-auto text-slate-300 mb-2" />
            <p className="text-sm text-slate-500 font-medium">Photo coming soon</p>
            <p className="text-xs text-slate-400 mt-1">{image.alt}</p>
          </div>
        )}
      </div>
      <div className="px-3 py-2 bg-slate-50">
        <p className="text-sm text-slate-600">{image.caption}</p>
      </div>
    </div>
  );
};

// Image gallery component
const ImageGallery: React.FC<{ images: HelperContentData['visualExamples'] }> = ({ images }) => {
  if (!images || images.length === 0) return null;
  
  return (
    <div className="space-y-3">
      {images.map((image, index) => (
        <HelperImage key={index} image={image} />
      ))}
    </div>
  );
};

const HelperContent: React.FC<HelperContentProps> = ({ content, category }) => {
  return (
    <div className="divide-y divide-slate-100">
      {/* Definition - What This Means */}
      {content.definition && (
        <Section title="What This Means" icon={BookOpen}>
          <p className="text-slate-600 leading-relaxed">{content.definition}</p>
        </Section>
      )}

      {/* Visual Examples */}
      {content.visualExamples && content.visualExamples.length > 0 && (
        <Section title="Visual Examples" icon={Camera}>
          <ImageGallery images={content.visualExamples} />
        </Section>
      )}

      {/* How to Check */}
      {content.howToCheck && content.howToCheck.length > 0 && (
        <Section title="How to Check" icon={ListChecks}>
          <ol className="space-y-3">
            {content.howToCheck.map((step, index) => (
              <li key={index} className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-slate-100 rounded-full flex items-center justify-center text-sm font-medium text-slate-600">
                  {index + 1}
                </span>
                <span className="text-slate-600 leading-6">{step}</span>
              </li>
            ))}
          </ol>
        </Section>
      )}

      {/* What to Look For */}
      {content.whatToLookFor && content.whatToLookFor.length > 0 && (
        <Section title="What to Look For" icon={Eye}>
          <ul className="space-y-2">
            {content.whatToLookFor.map((item, index) => (
              <li key={index} className="flex items-start gap-2 text-slate-600">
                <span className="text-teal-500 shrink-0 leading-6">•</span>
                <span className="leading-6">{item}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* Why It Matters in Texas */}
      {content.whyItMatters && (
        <Section title="Why This Matters in Texas" icon={MapPin}>
          <div className="bg-teal-50 rounded-lg p-4 border border-teal-100">
            <p className="text-teal-800 leading-relaxed">{content.whyItMatters}</p>
          </div>
        </Section>
      )}

      {/* Safety Warning */}
      {content.safetyWarning && (
        <div className="px-5 py-4">
          <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
            <div className="flex gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-amber-800 mb-1">Safety Warning</h4>
                <p className="text-amber-700 text-sm leading-relaxed">{content.safetyWarning}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Documentation Tips */}
      {content.documentationTips && content.documentationTips.length > 0 && (
        <Section title="Documentation Tips" icon={FileText}>
          <div className="bg-slate-50 rounded-lg p-4">
            <p className="text-sm text-slate-500 mb-3">
              Evidence that strengthens your appeal:
            </p>
            <ul className="space-y-2">
              {content.documentationTips.map((tip, index) => (
                <li key={index} className="flex gap-2 text-slate-600 text-sm">
                  <FileText className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
          </div>
        </Section>
      )}

      {/* Pro Tip */}
      {content.proTip && (
        <div className="px-5 py-4">
          <div className="bg-teal-50 rounded-lg p-4 border border-teal-200">
            <div className="flex gap-3">
              <Lightbulb className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-teal-800 mb-1">Pro Tip</h4>
                <p className="text-teal-700 text-sm leading-relaxed">{content.proTip}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HelperContent;
